/**
 * 认证状态管理 (Zustand)
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, LoginResponse } from '@/types/user'
import type { RouteConfig } from '@/types/route'
import { userApi, menuApi } from '@/api'
import type { MenuTreeNode } from '@/api/menu'
import { transformMenusToRoutes } from '@/utils/routeHelper'
import { staticRoutes } from '@/router'

interface PersistedAuthSnapshot {
  token: string | null
  user: User | null
  menus: MenuTreeNode[] | null
}

const getPersistedAuthSnapshot = (): PersistedAuthSnapshot => {
  if (typeof window === 'undefined') {
    return {
      token: null,
      user: null,
      menus: null,
    }
  }

  try {
    const raw = window.localStorage.getItem('auth-storage')
    if (!raw) {
      return {
        token: null,
        user: null,
        menus: null,
      }
    }

    const parsed = JSON.parse(raw)
    const state = parsed?.state

    return {
      token: state?.token ?? null,
      user: state?.user ?? null,
      menus: state?.menus ?? null,
    }
  } catch (error) {
    console.error('[authStore] Failed to parse persisted auth-storage:', error)
    return {
      token: null,
      user: null,
      menus: null,
    }
  }
}

const persistedAuth = getPersistedAuthSnapshot()
const persistedMenus = persistedAuth.menus
const initialDynamicRoutes = persistedMenus ? transformMenusToRoutes(persistedMenus) : []

interface AuthState {
  token: string | null
  user: User | null
  isAuthenticated: boolean
  menus: MenuTreeNode[] | null
  dynamicRoutes: RouteConfig[]
  isHydrated: boolean
  isRouteBootstrapDone: boolean
  isFetchingMenus: boolean
  setAuth: (token: string, user: User) => void
  setAuthFromLoginResponse: (response: LoginResponse) => void
  login: (username: string, password: string, captchaToken: string, captchaCode: string) => Promise<LoginResponse>
  logout: () => void
  updateUser: (user: User) => void
  fetchCurrentUser: () => Promise<void>
  fetchMenus: () => Promise<void>
  getAllRoutes: () => RouteConfig[]
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: persistedAuth.token,
      user: persistedAuth.user,
      isAuthenticated: Boolean(persistedAuth.token),
      menus: persistedMenus,
      dynamicRoutes: initialDynamicRoutes,
      isHydrated: true,
      isRouteBootstrapDone: !persistedAuth.token || initialDynamicRoutes.length > 0,
      isFetchingMenus: false,

      setAuth: (token, user) => {
        set({
          token,
          user,
          isAuthenticated: true,
          menus: null,
          dynamicRoutes: [],
          isRouteBootstrapDone: false,
        })
      },

      setAuthFromLoginResponse: (response) => {
        // 转换 RBAC 响应格式到 User 类型
        const user: User = {
          id: response.userId,
          user_name: response.userName,
          phone: response.phone,
          real_name: response.realName,
          is_activate: true,
        }
        get().setAuth(response.accessToken, user)
      },

      login: async (username, password, captchaToken, captchaCode) => {
        const response = await userApi.login({
          login_type: 'password',
          username,
          password,
          captcha_token: captchaToken,
          captcha_code: captchaCode,
          sys_group: 8,
        })

        // 需要两阶段验证时，先不写入登录态，交由页面处理
        if (response.status === 'mfa_required' || response.status === 'mfa_setup_required') {
          return response
        }

        get().setAuthFromLoginResponse(response)
        return response
      },

      logout: () => {
        // 清除状态和持久化存储
        set({
          token: null,
          user: null,
          isAuthenticated: false,
          menus: null,
          dynamicRoutes: [],
          isRouteBootstrapDone: true,
        })
      },

      updateUser: (user) => {
        set({ user })
      },

      fetchCurrentUser: async () => {
        const user = await userApi.getCurrentUser()
        set({ user })
      },

      fetchMenus: async () => {
        if (get().isFetchingMenus) {
          return
        }

        set({ isFetchingMenus: true })

        try {
          const response = await menuApi.getMe()
          const menus = response.menus
          // console.log('[authStore] Menus:', menus)
          // 将菜单转换为路由配置
          const dynamicRoutes = menus ? transformMenusToRoutes(menus) : []
          // console.log('[authStore] Dynamic routes:', dynamicRoutes)
          set({
            menus,
            dynamicRoutes,
            isAuthenticated: true,
            isRouteBootstrapDone: true,
            isFetchingMenus: false,
          })
        } catch (error) {
          set({
            isRouteBootstrapDone: true,
            isFetchingMenus: false,
          })
          throw error
        }
      },

      getAllRoutes: () => {
        const { dynamicRoutes } = get()
        // 合并静态路由和动态路由，404 放最后
        const nonStatic404 = staticRoutes.filter(r => r.path !== '*')
        const static404 = staticRoutes.find(r => r.path === '*')
        return [...nonStatic404, ...dynamicRoutes, ...(static404 ? [static404] : [])]
      },
    }),
    {
      name: 'auth-storage', // Zustand persist 存储键名
      partialize: (state) => ({ token: state.token, user: state.user, menus: state.menus }),
    }
  )
)

// 兼容性：同步 token 到 localStorage（供 request.ts 使用）
// 监听整个 store 的变化，同步 token 到 localStorage
useAuthStore.subscribe(
  (state, prevState) => {
    // 只有当 token 发生变化时才更新 localStorage
    if (state.token !== prevState?.token) {
      // console.log('[authStore] Token changed, saving to localStorage:', state.token ? 'YES' : 'NO')
      if (state.token) {
        localStorage.setItem('token', state.token)
        // console.log('[authStore] Token saved to localStorage')
      } else {
        localStorage.removeItem('token')
        // console.log('[authStore] Token removed from localStorage')
      }
    }
  }
)
